import { ExternalDestinationType } from "#gen/bundles/v1/bundle_pb.js";
import type { IconAsset } from "#lib/iconProvider.js";
import { formatPhoneNumber, normalizePhoneNumber } from "#lib/phonenumber.js";
import { mailtoProtocol, smsProtocol, telProtocol } from "#lib/url.js";

const bodyDelimiter = ";?&body=";

export function decodePhoneNumberDestination(destination: string): { phoneNumber: string; message: string } {
	if (!(destination?.startsWith(telProtocol) || destination?.startsWith(smsProtocol))) {
		return { phoneNumber: "", message: "" };
	}

	const currentDelimiter = bodyDelimiter;
	const legacyDelimiter = "?&body=";
	const isSMSLink = destination.startsWith(smsProtocol);
	const destinationWithoutProtocol = destination?.trim().slice(4);

	if (isSMSLink && (destinationWithoutProtocol.includes(legacyDelimiter) || destinationWithoutProtocol.includes(currentDelimiter))) {
		// The legacy delimiter is a substring of the current (correct)
		// delimiter, so we want to check for the current one first.
		const hasCurrentDelimiter = destinationWithoutProtocol.includes(currentDelimiter);
		const [phoneNumber, message] = destinationWithoutProtocol.split(hasCurrentDelimiter ? currentDelimiter : legacyDelimiter);
		return { phoneNumber: decodeURIComponent(phoneNumber), message: decodeURIComponent(message) };
	}

	return { phoneNumber: decodeURIComponent(destinationWithoutProtocol.split("?")[0]), message: "" };
}

export function decodeEmailDestination(destination: string): { emailAddress: string; subject: string; body: string; cc: string; bcc: string } {
	if (!destination.startsWith(mailtoProtocol)) {
		return { emailAddress: "", subject: "", body: "", cc: "", bcc: "" };
	}
	const cleanedEmailString = destination.trim().slice(7); //remove mailto:
	const emailParts = cleanedEmailString.split("?");
	const emailAddress = emailParts?.[0];
	const subjectParts = emailParts?.[1]?.split("&");
	let subject = "";
	let body = "";
	let cc = "";
	let bcc = "";
	subjectParts?.forEach(part => {
		if (part.startsWith("subject=")) {
			subject = part.slice(8);
		} else if (part.startsWith("body=")) {
			body = part.slice(5);
		} else if (part.startsWith("cc=")) {
			cc = part.slice(3);
		} else if (part.startsWith("bcc=")) {
			bcc = part.slice(4);
		}
	});

	return { emailAddress, subject: decodeURIComponent(subject), body: decodeURIComponent(body), cc: decodeURIComponent(cc), bcc: decodeURIComponent(bcc) };
}

export function encodePhoneNumberDestination(phoneNumber: string): string {
	if (stringContainsDataProperties(phoneNumber)) return `${telProtocol}${phoneNumber}`;
	const cleanedPhoneNumber = removeFormattingFromNumber(phoneNumber);
	return `${telProtocol}${cleanedPhoneNumber}`;
}

export function encodeSMSDestination(phoneNumber: string, message: string): string {
	const cleanedPhoneNumber = removeFormattingFromNumber(phoneNumber);
	const encodedMessage = encodeURIComponentOutsideOfDataProperties(message);
	return `${smsProtocol}${cleanedPhoneNumber}${message.length ? `${bodyDelimiter}${encodedMessage}` : ""}`;
}

export function encodeEmailDestination(emailAddress: string, subject: string, body: string, cc = "", bcc = ""): string {
	const encodedCc = encodeURIComponentOutsideOfDataProperties(cc);
	const encodedBcc = encodeURIComponentOutsideOfDataProperties(bcc);
	const encodedSubject = encodeURIComponentOutsideOfDataProperties(subject);
	const encodedBody = encodeURIComponentOutsideOfDataProperties(body);
	const linkPrefix = `${mailtoProtocol}${emailAddress}${cc.length || bcc.length || subject.length || body.length ? "?" : ""}`;
	return `${linkPrefix}${cc.length ? `cc=${encodedCc}` : ""}${cc.length && (bcc.length || subject.length || body.length) ? "&" : ""}${bcc.length ? `bcc=${encodedBcc}` : ""}${bcc.length && (subject.length || body.length) ? "&" : ""}${subject.length ? `subject=${encodedSubject}` : ""}${subject.length && body.length ? "&" : ""}${body.length ? `body=${encodedBody}` : ""}`;
}

export function formatPhoneNumberWithDashes(value: string) {
	if (stringContainsDataProperties(value)) return value;
	return formatPhoneNumber(value);
}

function stringContainsDataProperties(value: string): boolean {
	return /\{\{[^{}]*\}\}/.test(value);
}

function removeFormattingFromNumber(value: string) {
	if (stringContainsDataProperties(value)) return value;
	return normalizePhoneNumber(value).replace(/[^0-9+]/g, "");
}

export function getIconFromUrl(destinationType: ExternalDestinationType): IconAsset {
	switch (destinationType) {
		case ExternalDestinationType.PHONE:
			return { icon: "phone", iconSet: "heroicons" };
		case ExternalDestinationType.SMS:
			return { icon: "chat-bubble-left-ellipsis", iconSet: "heroicons" };
		case ExternalDestinationType.EMAIL:
			return { icon: "envelope", iconSet: "heroicons" };
		case ExternalDestinationType.DEEPLINK:
			return { icon: "squares-2x2", iconSet: "heroicons" };
		default:
			return { icon: "link", iconSet: "heroicons" };
	}
}

function encodeURIComponentOutsideOfDataProperties(message: string): string {
	const encodedMessage = message.replace(/{{(.*?)}}|([^{{}}]+)/g, (match, bracketed, nonBracketed) => {
		if (bracketed) {
			// If inside {{ }}, return it as is
			return `{{${bracketed}}}`;
		} else if (nonBracketed) {
			// Encode the non-bracketed part
			return encodeURIComponent(nonBracketed);
		}
		return match; // Fallback
	});
	return encodedMessage;
}
